import { useCallback, useEffect, useMemo } from "react";
import { useGlobalContext } from "../context/useGlobalContext";
import { useUser } from "./useUser";
import { getTenantRestaurants } from "../services/tenantOwnerService";
import { getTenantId } from "../utils/tenant";

const getRestoId = (resto) => String(resto?._id || resto?.id || resto?.place_id || resto?.restoId || "");

export const useOwnerRestaurantSelection = () => {
  const { isOwner } = useUser();
  const {
    resto,
    setResto,
    ownerRestaurants,
    setOwnerRestaurants,
    ownerRestaurantsLoading,
    setOwnerRestaurantsLoading,
    ownerRestaurantsLoaded,
    setOwnerRestaurantsLoaded,
    selectedOwnerRestaurantId,
    setSelectedOwnerRestaurantId,
  } = useGlobalContext();

  const loadOwnerRestaurants = useCallback(async (force = false) => {
    if (!isOwner) return;
    if (ownerRestaurantsLoading) return;
    if (!force && ownerRestaurantsLoaded) return;

    setOwnerRestaurantsLoading(true);
    try {
      const tenantId = getTenantId();
      if (!tenantId) {
        setOwnerRestaurants([]);
        return;
      }
      const response = await getTenantRestaurants(tenantId);
      const list = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];
      setOwnerRestaurants(list);
    } catch (_) {
      setOwnerRestaurants([]);
    } finally {
      setOwnerRestaurantsLoaded(true);
      setOwnerRestaurantsLoading(false);
    }
  }, [
    isOwner,
    ownerRestaurantsLoading,
    ownerRestaurantsLoaded,
    setOwnerRestaurants,
    setOwnerRestaurantsLoaded,
    setOwnerRestaurantsLoading,
  ]);

  useEffect(() => {
    loadOwnerRestaurants();
  }, [loadOwnerRestaurants]);

  useEffect(() => {
    if (!isOwner) return;
    if (!ownerRestaurants.length) return;

    const hasSelected = ownerRestaurants.some(
      (item) => getRestoId(item) === String(selectedOwnerRestaurantId)
    );

    if (!selectedOwnerRestaurantId || !hasSelected) {
      setSelectedOwnerRestaurantId(getRestoId(ownerRestaurants[0]));
    }
  }, [isOwner, ownerRestaurants, selectedOwnerRestaurantId, setSelectedOwnerRestaurantId]);

  const selectedOwnerRestaurant = useMemo(() => {
    if (!ownerRestaurants.length) return null;
    return (
      ownerRestaurants.find((item) => getRestoId(item) === String(selectedOwnerRestaurantId)) ||
      ownerRestaurants[0]
    );
  }, [ownerRestaurants, selectedOwnerRestaurantId]);

  useEffect(() => {
    if (!isOwner) return;
    if (!selectedOwnerRestaurant) return;

    const selectedId = getRestoId(selectedOwnerRestaurant);
    const currentId = getRestoId(resto);
    if (selectedId !== currentId) {
      setResto(selectedOwnerRestaurant);
    }
  }, [isOwner, selectedOwnerRestaurant, resto, setResto]);

  return {
    ownerRestaurants,
    ownerRestaurantsLoading,
    selectedOwnerRestaurantId,
    setSelectedOwnerRestaurantId,
    selectedOwnerRestaurant,
    reloadOwnerRestaurants: loadOwnerRestaurants,
  };
};
